import React from 'react';
import type { RoutePath } from '@shared/core/router.helper';
import { RoutesProvider } from '@shared/core/router.helper';
import type { RouteComponentProps } from '@react-router';
import { Redirect } from '@react-router';
import { useQueryParamHelper } from '@shared/core/queryString';
import { ProtectedAdminRoute } from '@apps/admin/components/ProtectedAdminRoute';
import { CREATE_WEDDING_APP_NAME } from '@apps/acquisitionFunnels/createWedding/constants';
import { Suspense } from '@shared/core/react/Suspense';
import { SERVICE_CENTER_APP_NAME } from '@apps/card/serviceCenter/constants';
import { SAVE_THE_DATE_PHOTO_ECARD_TYPE } from '@apps/admin/saveTheDate/constants';
import { getEcardType } from '@apps/admin/saveTheDate/SaveTheDate.utils';
import EcardError from '@shared/components/EmailsAndEcards/components/Error';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { CREATE_BABY_REGISTRY_APP_NAME } from '@apps/acquisitionFunnels/createBabyRegistry/constants';
import PageNotFound from '@shared/components/PageNotFound/PageNotFound';
import { LazyLogoutPage } from '@apps/admin/logout/LogoutPage.lazy';
import { EventRoute } from './EventRoute';
// Shared loadables (used in both CSR and SSR)
import { LazyAdmin } from '@apps/admin/Admin.lazy';
import { LazyGuest } from '@apps/guest/Guest.lazy';
import { LazyAccount } from '@apps/account/Account.lazy';
import { LazyShop } from '@apps/registry/public/routes/Shop/Shop.lazy';
import { LazyAccountMe } from '@apps/admin/accountMe/AccountMe.lazy';
import { LazyCreateWedding } from '@apps/acquisitionFunnels/createWedding/CreateWedding.lazy';
import { LazyCreateBabyRegistry } from '@apps/acquisitionFunnels/createBabyRegistry/CreateBabyRegistry.lazy';
import { LazyEventRedirect } from '@apps/admin/routes/EventRedirect/EventRedirect.lazy';
import { LazyEcardGuest } from '@apps/ecard/guest/EcardGuest.lazy';
import { LazySaveTheDateGuest } from '@apps/admin/saveTheDate/guest/SaveTheDateGuest.lazy';
import { LazyServiceCenter } from '@apps/card/serviceCenter/ServiceCenter.lazy';
import { LazyPublicShop } from '@apps/card/routes/PublicShop/PublicShop.lazy';
import { LazyCreateGeneralEvent } from '@apps/acquisitionFunnels/createGeneralEvent/CreateGeneralEvent.lazy';
import { LazyPublicStays } from '@apps/stays/PublicStays/PublicStays.lazy';
import { LazyRegistryGuest } from '@apps/registry/guest/RegistryGuest.lazy';

// CSR-only loadables
import { LazyWorkspace } from '@apps/admin/workspace/Workspace.lazy';
import { LazyBookmarklet } from '@apps/registry/admin/routes/Bookmarklet/Bookmarklet.lazy';
import { LazyCugRedirect } from '@apps/admin/cugRedirect/CugRedirect.lazy';
import { LazyRedirectToRetailer } from '@apps/registry/guest/routes/RedirectToRetailer/RedirectToRetailer.lazy';
import { LazyPopupTrigger } from '@apps/registry/popup-trigger/PopupTrigger.lazy';
import { LazyEmailPreferences } from '@apps/emailPreferences/EmailPreferences.lazy';
import { LazyThanksForRsvp } from '@apps/guest/thanksForRsvp/ThanksForRsvp.lazy';
import { LazyGreetingCard } from '@apps/card/greetingCard/GreetingCard.lazy';
import { LazyContactCollectorIframe } from '@apps/admin/contactCollector/iframe/ContactCollectorIframe.lazy';
import { LazyRegistrationGuest } from '@apps/guest/routes/registration/RegistrationGuest.lazy';
import { LazyDashboardPreview } from '@apps/admin/routes/DashboardPreview/DashboardPreview.lazy';

const RootGuestSiteRoute: React.FC<RouteComponentProps<{ eventHandle: string }>> = ({ match }) => {
  const { getValueString } = useQueryParamHelper();

  if (match) {
    if (getValueString('preview') === 'true' && getValueString('ctx') === 'admindashboard') {
      // Now that we're rendering the entire admin dashboard preview panel in joy-web,
      // we leverage the 'ctx' query param in rendering
      const postMessageShareClicked = () => {
        sendMessageToParentWindow({ action: 'previewActionInteraction', value: 'shareButton', source: 'joyPreview' });
      };
      return (
        <Suspense fallback={<></>}>
          <ProtectedAdminRoute
            disableDefaultLoader={true}
            isNonEditAdminRoute={true}
            path={`/${match.params.eventHandle}`}
            loader={<></>}
            render={() => {
              return (
                <LazyDashboardPreview
                  isSitePreview
                  eventHandle={match.params.eventHandle}
                  handleShareDialogOpen={postMessageShareClicked}
                />
              );
            }}
          />
        </Suspense>
      );
    }

    // We are handling redirection here: https://github.com/joylifeinc/joy-web/blob/b22185084f3a63796d37f0f14d44df6e7a235fe8/src/apps/guest/routes/GuestSite/GuestSite.utils.ts#L82
    // window.location.href = window.location.origin + `/${match.params.eventHandle}/edit`;

    return <LazyGuest />;
  }

  // Redirect to root route
  window.location.href = window.location.origin;
  return null;
};

export const routes: Array<RoutePath<{ eventHandle: string; ecardId: string; firebaseEventId: string; formId: string; contactId: string }>> = [
  {
    path: '/:eventHandle/registry/bookmarklet/popup-trigger',
    component: () => <LazyPopupTrigger />,
  },
  {
    path: '/account/(events|personal-info)',
    component: () => <LazyAccountMe appUrl={'account'} />,
  },
  {
    path: '/account',
    component: () => <LazyAccount appUrl="account" />,
  },
  {
    path: '/account-callback',
    component: () => <LazyAccount appUrl="account-callback" />,
  },
  {
    path: '/account-login',
    component: () => <LazyAccount appUrl="account-login" />,
  },
  {
    path: '/social-login',
    component: () => <LazyAccount appUrl="social-login" />,
  },
  {
    path: `/${CREATE_WEDDING_APP_NAME}`,
    component: () => <LazyCreateWedding />,
  },
  {
    path: `/${CREATE_BABY_REGISTRY_APP_NAME}`,
    component: () => <LazyCreateBabyRegistry />,
  },
  {
    path: '/ecard',
    component: ({ location }) => {
      const ecardType = getEcardType(location);
      return ecardType === undefined ? <EcardError /> : ecardType === SAVE_THE_DATE_PHOTO_ECARD_TYPE ? <LazySaveTheDateGuest /> : <LazyEcardGuest />;
    },
  },
  {
    path: '/createevent',
    component: () => <LazyCreateGeneralEvent />,
  },
  {
    path: `/email-preferences`,
    component: () => <LazyEmailPreferences />,
  },
  // Public shop is generally SSR, but we can also serve it CSR when using the `feature.joy-web` FF.
  {
    path: '/shop',
    component: () => (
      <RoutesProvider>
        <LazyShop />
      </RoutesProvider>
    ),
  },
  {
    path: '/cugnotverified',
    component: () => <LazyCugRedirect />,
  },
  {
    path: '/notfound',
    component: () => <PageNotFound />,
  },
  {
    path: `/${SERVICE_CENTER_APP_NAME}`, //other subroutes?
    component: () => <LazyServiceCenter />,
  },
  {
    path: '/card/:ecardId([A-Za-z0-9-]{11,})',
    component: ({ match }) => <LazyGreetingCard ecardId={match.params.ecardId} />,
  },
  // always put eventHandle routes last to allow above routes to match
  // {
  //   path: '/:eventHandle/showcase',
  //   component: ({ match }) => (match ? <GuestShowcaseRoute eventHandle={match.params.eventHandle} /> : null)
  // },
  {
    path: '/cards',
    component: () => <LazyPublicShop />,
  },
  {
    path: '/card',
    component: ({ location }) => {
      return <Redirect to={{ pathname: '/cards', search: location.search }} />;
    },
  },
  {
    path: '/contact-iframe/:firebaseEventId/update/:contactId',
    component: ({ match }) =>
      match ? (
        <RoutesProvider>
          <LazyContactCollectorIframe
            firebaseEventId={match.params.firebaseEventId}
            contactId={match.params.contactId}
          />
        </RoutesProvider>
      ) : null,
  },
  {
    path: '/contact-iframe/:firebaseEventId',
    component: ({ match }) =>
      match ? (
        <RoutesProvider>
          <LazyContactCollectorIframe firebaseEventId={match.params.firebaseEventId} />
        </RoutesProvider>
      ) : null,
  },
  {
    path: '/travel',
    component: () => <LazyPublicStays />,
  },
  {
    path: '/event',
    // second agent
    exact: true,
    component: () => <LazyEventRedirect />,
  },
  {
    path: '/event/edit',
    component: () => <LazyEventRedirect />,
  },
  {
    path: '/logout',
    component: () => <LazyLogoutPage />,
  },
  {
    path: '/:eventHandle/registration/:formId',
    component: ({ match }) =>
      match.params.eventHandle && match.params.formId ? (
        <RoutesProvider>
          <LazyRegistrationGuest
            eventHandle={match.params.eventHandle}
            formId={match.params.formId}
          />
        </RoutesProvider>
      ) : null,
  },
  {
    path: '/:eventHandle/thanksForRsvp',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyThanksForRsvp}
      />
    ),
  },
  {
    path: '/:eventHandle/registry/retailer-redirect',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRedirectToRetailer}
      />
    ),
  },
  {
    path: '/:eventHandle/registry',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRegistryGuest}
      />
    ),
  },
  {
    path: '/:eventHandle/edit/registry/bookmarklet',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyBookmarklet}
      />
    ),
  },
  {
    path: '/:eventHandle/edit',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyAdmin}
      />
    ),
  },
  {
    path: '/:eventHandle/workspace',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyWorkspace}
      />
    ),
  },
  {
    path: '/:eventHandle',
    component: props => (
      <EventRoute eventHandle={props.match.params.eventHandle}>
        <RootGuestSiteRoute {...props} />
      </EventRoute>
    ),
  },
];
